import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { validateCoordinates } from '@nx-smartmonkey/shared/helpers';
import { RoutalLoader as Loader, RoutalPalette } from '@nx-smartmonkey/ui';
import lodash from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useRetrieveActiveLanguage } from '../../../../hooks/translations/useRetrieveActiveLanguage';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { Text } from '../../../../react-components/Text';
import { getAutocompleteGeocode, getLocationFromCoordinates, getLocationFromId } from '../../../../services/requests';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { LocationPill } from './LocationPill';

let sessionToken: string | undefined;

type AddStopsListProps = {
  searchText: string;
  icon: `add` | `remove`;
  styles?: React.CSSProperties;
  onSelectLocation: (location: Location) => void;
  refreshSession: boolean;
};

export const AddStopsList = ({ searchText, styles, icon, onSelectLocation, refreshSession }: AddStopsListProps) => {
  const { data: vehicleData } = useRetrieveVehicle();

  const [loading, setLoading] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>([]);

  const [endSession, setEndSession] = useState<boolean>(false);
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const [lastText, setLastText] = useState<string>(``);
  const { activeLanguage } = useRetrieveActiveLanguage();

  useEffect(() => {
    if (refreshSession && endSession) setEndSession(false);
  }, [refreshSession, endSession]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOptionsDelayed = useCallback(
    lodash.debounce(async (text, callback) => {
      try {
        const inputValueSplited = text.split(`,`);
        let newOptions;
        if (
          inputValueSplited.length === 2 &&
          validateCoordinates({ lat: Number(inputValueSplited[0]), lng: Number(inputValueSplited[1]) })
        ) {
          newOptions = await getLocationFromCoordinates({
            coordinates: [
              {
                lat: Number(inputValueSplited[0]),
                lng: Number(inputValueSplited[1]),
              },
            ],
            lang: activeLanguage,
          });
        } else {
          if (!sessionToken) sessionToken = uuidv4();
          newOptions = await getAutocompleteGeocode({
            searchValue: text,
            projectId: vehicleData?.project.id,
            sessionToken,
          });
        }
        callback(newOptions);
      } catch (error) {
        console.error(`ERROR: `, error);
        showSnackbarMessage({
          messageIntlId: `infoerror.highway.user.error.location_search`,
        });
        setLoading(false);
        setLocations([]);
      }
    }, 500),
    [sessionToken]
  );

  useEffect(() => {
    let active = true;

    if (active && searchText !== `` && searchText !== lastText && !endSession) {
      setLoading(true);
      setLocations([]);
      getOptionsDelayed(searchText, (newOptions: any[]) => {
        setLocations(newOptions);
        setLoading(false);
        setLastText(searchText);
      });
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, getOptionsDelayed]);

  if (loading) {
    return (
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        style={{
          height: `55px`,
          ...styles,
        }}
      >
        <Loader style={{ width: `16px`, height: `auto`, padding: 0 }} />
        <Text intlId="drivers.loading" style={{ marginLeft: `5px`, color: RoutalPalette.grey11 }} />
      </Grid>
    );
  }

  if (locations.length === 0) {
    return (
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        style={{
          height: `55px`,
          ...styles,
        }}
      >
        <Text intlId="driver.add_stops.search.no_results" style={{ color: RoutalPalette.grey11 }} />
      </Grid>
    );
  }

  const onAddLocation = async (location: Location) => {
    if (location?.lat && location?.lng) {
      await onSelectLocation(location);
    } else if (location.location_id) {
      if (!sessionToken && !endSession) sessionToken = uuidv4(); //This is in case you want the information of a location searched in another session.
      try {
        const resp = await getLocationFromId(location.location_id, sessionToken);
        sessionToken = undefined;
        setEndSession(true);
        await onSelectLocation(Location.create(resp));
      } catch (error) {
        console.info(`ERROR: `, error);
        showSnackbarMessage({
          messageIntlId: `infoerror.highway.user.error.too_many_requests`,
        });
      }
    }
  };

  return (
    <Grid style={styles}>
      {locations.map((location) => (
        <LocationPill
          icon={icon}
          key={location.location_id}
          location={location}
          onClickOverIcon={() => {
            onAddLocation(location);
          }}
          onSelectLocation={() => {
            onAddLocation(location);
          }}
        />
      ))}
    </Grid>
  );
};
